import { useCallback, useEffect, useState } from "react";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "./DataTable";
import ActionCell from "./components/ActionCell";
import { CompanyService } from "services/companyService";
import { GeneralJournalService } from "services/general-journal";
import { formatDate, formatVndIntegerValue } from "utils";
import { toast } from "react-toastify";

const renderData = (items = [], setDataTable, setPaginationData) =>
  items.map((item, index) => {
    const mappedRow = {
      id: item._id,
      stt: index + 1,
      year: item.year || "",
      fromDate: formatDate(item.fromDate),
      toDate: formatDate(item.toDate),
      rowCount: item.rowCount || 0,
      totalJournalAmount: formatVndIntegerValue(item.totalJournalAmount || 0),
      createdAt: formatDate(item.createdAt),
    };

    return {
      ...mappedRow,
      action: (
        <ActionCell
          item={mappedRow}
          setDataTable={setDataTable}
          setPaginationData={setPaginationData}
        />
      ),
    };
  });

const tableData = {
  columns: [
    { Header: "STT", accessor: "stt", align: "center" },
    { Header: "Năm", accessor: "year", align: "center" },
    { Header: "Từ ngày", accessor: "fromDate", align: "center" },
    { Header: "Đến ngày", accessor: "toDate", align: "center" },
    { Header: "Số dòng", accessor: "rowCount", align: "center" },
    { Header: "Tổng phát sinh", accessor: "totalJournalAmount", align: "right" },
    { Header: "Ngày tạo", accessor: "createdAt", align: "center" },
    { Header: "Actions", accessor: "action", align: "center", sorted: false },
  ],
  rows: [],
};

const getDefaultYear = () => String(new Date().getFullYear());

export default function GeneralJournalLedger() {
  const [loading, setLoading] = useState(false);
  const [companyId, setCompanyId] = useState("");
  const [dataTable, setDataTable] = useState(tableData);
  const [query, setQuery] = useState({
    sortBy: "createdAt",
    sortType: "desc",
    searchBy: "",
    year: getDefaultYear(),
    fromDate: "",
    toDate: "",
  });
  const [paginationData, setPaginationData] = useState({ page: 1, count: 0, size: 10 });

  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [createFromDate, setCreateFromDate] = useState("");
  const [createToDate, setCreateToDate] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const fetchData = useCallback(async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      const response = await GeneralJournalService.getS01DnRecords({
        company: companyId,
        year: query.year ? Number(query.year) : undefined,
        fromDate: query.fromDate || undefined,
        toDate: query.toDate || undefined,
        page: paginationData.page,
        limit: paginationData.size,
      });
      const payload = response?.data || response || {};
      const items = payload?.items || [];
      const paginate = payload?.paginate || {};
      setPaginationData((prev) => ({
        ...prev,
        count: paginate?.count || 0,
        size: paginate?.size || prev.size,
      }));
      setDataTable((prev) => ({
        ...prev,
        rows: renderData(items, setDataTable, setPaginationData),
      }));
    } catch (error) {
      setDataTable((prev) => ({ ...prev, rows: [] }));
      setPaginationData((prev) => ({ ...prev, count: 0 }));
    } finally {
      setLoading(false);
    }
  }, [companyId, paginationData.page, paginationData.size, query.fromDate, query.toDate, query.year]);

  useEffect(() => {
    const init = async () => {
      const companyResponse = await CompanyService.getByAuth();
      const company = companyResponse?.data || {};
      setCompanyId(company?._id || "");
    };
    init();
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPaginationData((prev) => ({ ...prev, page: 1 }));
  }, [query.year, query.fromDate, query.toDate]);

  const handlePreview = async () => {
    if (!createFromDate || !createToDate) {
      toast.error("Vui lòng chọn từ ngày và đến ngày");
      return;
    }
    if (createFromDate > createToDate) {
      toast.error("Khoảng thời gian không hợp lệ");
      return;
    }
    try {
      setPreviewLoading(true);
      const response = await GeneralJournalService.previewS01Dn({
        company: companyId,
        year: Number(query.year),
        fromDate: createFromDate,
        toDate: createToDate,
      });
      const payload = response?.data || response || {};
      setPreviewData(payload);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Không preview được dữ liệu");
      setPreviewData(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!previewData) {
      toast.error("Vui lòng preview trước khi tạo");
      return;
    }
    try {
      setCreateLoading(true);
      await GeneralJournalService.createS01Dn({
        company: companyId,
        year: Number(query.year),
        fromDate: createFromDate,
        toDate: createToDate,
      });
      toast.success("Tạo sổ S01-DN thành công");
      setOpenCreateDialog(false);
      setCreateFromDate("");
      setCreateToDate("");
      setPreviewData(null);
      setPaginationData((prev) => ({ ...prev, page: 1 }));
      await fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Tạo sổ thất bại");
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox my={3}>
        <Card style={{ height: "100%" }}>
          <DataTable
            query={query}
            table={dataTable}
            loading={loading}
            setQuery={setQuery}
            paginationData={paginationData}
            setPaginationData={setPaginationData}
            onOpenCreateDialog={() => setOpenCreateDialog(true)}
          />
        </Card>
      </SoftBox>

      <Dialog
        open={openCreateDialog}
        onClose={() => {
          if (!createLoading && !previewLoading) {
            setOpenCreateDialog(false);
            setPreviewData(null);
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Tạo Nhật ký - Sổ cái (S01-DN)</DialogTitle>
        <DialogContent>
          <SoftBox mt={1} display="flex" gap={2} flexWrap="wrap" alignItems="flex-end">
            <SoftBox>
              <SoftTypography variant="caption" fontWeight="bold">
                Từ ngày
              </SoftTypography>
              <SoftInput
                type="date"
                value={createFromDate}
                onChange={({ target }) => setCreateFromDate(target.value)}
              />
            </SoftBox>
            <SoftBox>
              <SoftTypography variant="caption" fontWeight="bold">
                Đến ngày
              </SoftTypography>
              <SoftInput
                type="date"
                value={createToDate}
                onChange={({ target }) => setCreateToDate(target.value)}
              />
            </SoftBox>
            <SoftButton
              variant="outlined"
              color="dark"
              disabled={previewLoading || createLoading}
              onClick={handlePreview}
            >
              {previewLoading ? "ĐANG PREVIEW..." : "PREVIEW"}
            </SoftButton>
          </SoftBox>

          {previewData ? (
            <SoftBox mt={2} p={2} sx={{ border: "1px solid #e0e0e0", borderRadius: "0.5rem" }}>
              <SoftTypography variant="button" fontWeight="bold">
                Kết quả preview
              </SoftTypography>
              <SoftTypography variant="button" display="block">
                Số dòng: {previewData?.summary?.rowCount || 0}
              </SoftTypography>
              <SoftTypography variant="button" display="block">
                Tổng phát sinh:{" "}
                {formatVndIntegerValue(previewData?.summary?.totalJournalAmount || 0)}
              </SoftTypography>
            </SoftBox>
          ) : null}
        </DialogContent>
        <DialogActions>
          <SoftButton
            variant="outlined"
            color="dark"
            disabled={createLoading || previewLoading}
            onClick={() => {
              setOpenCreateDialog(false);
              setPreviewData(null);
            }}
          >
            Hủy
          </SoftButton>
          <SoftButton
            variant="gradient"
            color="dark"
            disabled={!previewData || createLoading || previewLoading}
            onClick={handleCreate}
          >
            {createLoading ? "ĐANG TẠO..." : "TẠO"}
          </SoftButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
